'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useToast } from '@/contexts/ToastContext';

interface TwoFactorVerifyProps {
  onVerified: () => void;
  onCancel: () => void;
}

export default function TwoFactorVerify({ onVerified, onCancel }: TwoFactorVerifyProps) {
  const { showSuccess, showError } = useToast();
  const [token, setToken] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await fetch('/api/auth/2fa/verify-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, trustDevice })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Invalid verification code');
      }

      const data = await response.json();
      
      // Store device token if trusted
      if (data.deviceToken) {
        localStorage.setItem('trusted_device_token', data.deviceToken);
      }

      showSuccess('Verification successful!');
      onVerified();
    } catch (error: any) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
      <div className="flex items-center justify-center mb-6">
        <div className="p-4 rounded-full" style={{ background: 'var(--bg-primary)' }}>
          <Icon name="ShieldCheckIcon" className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-center mb-2" style={{ color: 'var(--text-primary)' }}>
        Two-Factor Authentication
      </h2>
      <p className="text-sm text-center mb-6" style={{ color: 'var(--text-secondary)' }}>
        {useRecoveryCode ? 'Enter a recovery code' : 'Enter the 6-digit code from your authenticator app'}
      </p>

      <form onSubmit={handleVerify}>
        <div className="mb-4">
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(useRecoveryCode ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={useRecoveryCode ? 'XXXXXXXX' : '000000'}
            maxLength={useRecoveryCode ? 8 : 6}
            className="w-full px-4 py-3 rounded-lg text-center text-2xl font-mono tracking-widest"
            style={{
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-primary)',
              color: 'var(--text-primary)'
            }}
            required
            autoFocus
          />
        </div>

        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              className="w-4 h-4 rounded"
              style={{ accentColor: 'var(--accent-primary)' }}
            />
            <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
              Trust this device for 30 days
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={loading || token.length < (useRecoveryCode ? 8 : 6)}
          className="w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 mb-3"
          style={{ background: 'var(--accent-primary)', color: 'white' }}
        >
          {loading ? 'Verifying...' : 'Verify'}
        </button>

        <button
          type="button"
          onClick={() => setUseRecoveryCode(!useRecoveryCode)}
          className="w-full py-2 text-sm hover:opacity-70 transition-opacity"
          style={{ color: 'var(--accent-primary)' }}
        >
          {useRecoveryCode ? 'Use authenticator code' : 'Use recovery code'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="w-full py-2 text-sm hover:opacity-70 transition-opacity mt-2"
          style={{ color: 'var(--text-secondary)' }}
        >
          Cancel
        </button>
      </form>
    </div>
  );
}